"use client";

import type { AxiosInstance } from "axios";
import { URL } from "api";
import { useUserStore } from "@/_nucleo/store/_auth.store";
import type {
  ApiInstanceConfig,
  ApiInstancesMap,
  TokenProvider,
  CustomRequestOptions,
  ApiResponse,
} from "api";
import {
  createAxiosInstance,
  setupRequestInterceptor,
  setupResponseInterceptor,
  createDefaultApiConfig,
  mergeRequestConfig,
} from "api";
import { redirect } from "next/navigation";

const getTenantId = async (): Promise<string> => {
  const { filiais } = useUserStore.getState();

  if (!filiais || filiais.length === 0) {
    return "default";
  }

  // Verifica se já tem um tenantId salvo que funcionou
  const auth = useUserStore.getState().auth;
  if (auth?.tenantId && auth.tenantId !== "default") {
    return auth.tenantId;
  }

  // Se não tem tenantId salvo, pega a primeira filial
  const primeiraFilial = filiais[0];
  if (primeiraFilial?.codigo) {
    const empresaCodigo = primeiraFilial.codigo.slice(0, 2);
    const filialCodigo = primeiraFilial.codigo;
    const tenantId = `${empresaCodigo},${filialCodigo}`;
    return tenantId;
  }

  return "default";
};

const getNextTenantId = async (): Promise<string> => {
  const { filiais } = useUserStore.getState();
  const currentTenantId = useUserStore.getState().auth?.tenantId;

  if (!filiais || filiais.length === 0) {
    return "default";
  }

  if (currentTenantId && currentTenantId !== "default") {
    const currentTenantParts = currentTenantId.split(",");
    const currentEmpresaCodigo = currentTenantParts[0];
    const currentFilialCodigo = currentTenantParts[1];

    const nextFilialIndex = filiais.findIndex(
      (filial) => filial.codigo === currentFilialCodigo
    );

    if (nextFilialIndex !== -1 && nextFilialIndex < filiais.length - 1) {
      const nextFilial = filiais[nextFilialIndex + 1];
      const nextEmpresaCodigo = nextFilial.codigo.slice(0, 2);
      const nextFilialCodigo = nextFilial.codigo;
      return `${nextEmpresaCodigo},${nextFilialCodigo}`;
    }
  }

  const primeiraFilial = filiais[0];
  if (primeiraFilial?.codigo) {
    const primeiraEmpresaCodigo = primeiraFilial.codigo.slice(0, 2);
    const primeiraFilialCodigo = primeiraFilial.codigo;
    return `${primeiraEmpresaCodigo},${primeiraFilialCodigo}`;
  }

  return "default";
};

const getAuthToken = async (): Promise<string | null> => {
  return useUserStore.getState().auth?.token ?? null;
};

const API_CONFIGS: ApiInstancesMap = {
  production: createDefaultApiConfig(URL.in.API_PRODUCTION, true),
  avatar: createDefaultApiConfig(URL.out.API_AVATAR_URL, false, {
    headers: {
      "X-Uploadthing-Api-Key": `${URL.out.API_AVATAR_SECRET_KEY}`,
    },
  }),
  internal: createDefaultApiConfig(
    typeof window !== "undefined" ? window.location.origin : "",
    false
  ),
};

function createConfiguredInstance(
  config: ApiInstanceConfig,
  tokenProvider: TokenProvider
): AxiosInstance {
  const instance = createAxiosInstance(config);

  if (config.requiresAuth) {
    setupRequestInterceptor(instance, {
      tokenProvider,
      authRequiredUrls: [config.baseURL],
      tenantIdProvider: getTenantId,
    });
    setupResponseInterceptor(instance, {
      onAuthError: () => {
      redirect("/login");      
      },
      logger: (message, data) => {
        // Mantém logs silenciosos em produção
        if (process.env.NODE_ENV === "development") {
          console.log(`📡 [ApiClient] ${message}`, data);
        }
      },
      tokenProvider,
    });
  }

  return instance;
}

export const apiClient = {
  production: createConfiguredInstance(API_CONFIGS.production, getAuthToken),
  avatar: createConfiguredInstance(API_CONFIGS.avatar, () =>
    Promise.resolve(URL.out.API_AVATAR_SECRET_KEY)
  ),
  internal: createConfiguredInstance(API_CONFIGS.internal, () =>
    Promise.resolve(null)
  ),
} as const;

export function createCustomInstance(
  config: ApiInstanceConfig,
  tokenProvider: TokenProvider = getAuthToken
): AxiosInstance {
  return createConfiguredInstance(config, tokenProvider);
}

export async function get<T = unknown>(
  instance: AxiosInstance,
  url: string,
  options: CustomRequestOptions = {}
): Promise<ApiResponse<T>> {
  const config = mergeRequestConfig({ method: "GET", url }, options);
  return instance.request<T>(config);
}

export async function post<T = unknown>(
  instance: AxiosInstance,
  url: string,
  data?: unknown,
  options: CustomRequestOptions = {}
): Promise<ApiResponse<T>> {
  const config = mergeRequestConfig({ method: "POST", url, data }, options);
  return instance.request<T>(config);
}

export async function put<T = unknown>(
  instance: AxiosInstance,
  url: string,
  data?: unknown,
  options: CustomRequestOptions = {}
): Promise<ApiResponse<T>> {
  const config = mergeRequestConfig({ method: "PUT", url, data }, options);
  return instance.request<T>(config);
}

export const httpMethods = {
  GET: "GET" as const,
  POST: "POST" as const,
} as const;

// Exporta as funções de tenant para uso externo
export { getTenantId, getNextTenantId };
